import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import { listZGenerations, zGenerationReusePath, zGenerationWizardPath, duplicateZProject, type ZStudioGeneration } from '@/lib/zstudio';

// Historique des générations IA (spec 56) — conserver chaque génération
// et permettre de la réutiliser pour relancer une création.

const TYPE_META: Record<string, { label: string; icon: string; className: string }> = {
  image: { label: 'Image', icon: 'ri-image-line', className: 'bg-accent-100 text-accent-700' },
  video: { label: 'Vidéo', icon: 'ri-film-line', className: 'bg-primary-100 text-primary-700' },
  campaign: { label: 'Campagne', icon: 'ri-rocket-line', className: 'bg-primary-100 text-primary-700' },
  ad_copy: { label: 'Texte pub', icon: 'ri-megaphone-line', className: 'bg-secondary-100 text-secondary-800' },
  hashtags: { label: 'Hashtags', icon: 'ri-hashtag', className: 'bg-secondary-100 text-secondary-800' },
  slogan: { label: 'Slogan', icon: 'ri-quill-pen-line', className: 'bg-secondary-100 text-secondary-800' },
  tryon: { label: 'Try-on', icon: 'ri-user-star-line', className: 'bg-accent-100 text-accent-700' },
  edit: { label: 'Édition IA', icon: 'ri-equalizer-2-line', className: 'bg-accent-100 text-accent-700' },
  edit_image: { label: 'Édition IA', icon: 'ri-equalizer-2-line', className: 'bg-accent-100 text-accent-700' },
  lookbook: { label: 'Lookbook', icon: 'ri-book-open-line', className: 'bg-secondary-100 text-secondary-800' },
};

const STATUS_META: Record<string, { label: string; className: string; icon: string }> = {
  done: { label: 'Terminé', className: 'bg-accent-100 text-accent-700', icon: 'ri-checkbox-circle-line' },
  error: { label: 'Erreur', className: 'bg-red-100 text-red-600', icon: 'ri-error-warning-line' },
  generating: { label: 'En cours', className: 'bg-secondary-100 text-secondary-800', icon: 'ri-loader-4-line' },
};

function outputUrl(g: ZStudioGeneration): string | null {
  const out = g.output;
  if (out && typeof out === 'object' && typeof (out as { url?: unknown }).url === 'string') {
    return (out as { url: string }).url;
  }
  return null;
}

function outputText(g: ZStudioGeneration): string {
  const out = g.output;
  if (!out || typeof out !== 'object') return '';
  const obj = out as Record<string, unknown>;
  if (typeof obj.caption === 'string') return obj.caption;
  if (Array.isArray(obj.tags)) return (obj.tags as string[]).join(' ');
  if (typeof obj.concept === 'string') return obj.concept;
  return '';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function ZStudioHistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [generations, setGenerations] = useState<ZStudioGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      setGenerations(await listZGenerations(user.id, 60));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  // Réutiliser une génération précédente : on rouvre le studio spécialisé
  // correspondant (Try-on / Édition IA / Lookbook) ou, à défaut, le wizard
  // pré-rempli pour relancer une création en un clic.
  const reuse = (g: ZStudioGeneration) => {
    navigate(zGenerationReusePath(g));
  };

  // Dupliquer : relance à l'identique SANS repasser par le formulaire.
  // On clone le projet associé (copie indépendante) puis on ouvre le wizard
  // déjà pré-rempli avec la même consigne.
  const duplicate = async (g: ZStudioGeneration) => {
    setBusyId(g.id);
    try {
      if (g.project_id) await duplicateZProject(g.project_id);
    } catch { /* la copie a échoué mais on laisse relancer */ }
    finally { setBusyId(null); }
    navigate(zGenerationWizardPath(g));
  };

  if (!user?.id) {
    return <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <Link to="/dashboard/zstudio" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-4 cursor-pointer">
        <i className="ri-arrow-left-line"></i>ZStudio
      </Link>

      <div className="mb-5">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-history-line text-accent-500"></i>Historique des générations
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Chaque génération IA est conservée — réutilisez-en une pour relancer une création en un clic.</p>
      </div>

      <ZStudioNav />

      {error && <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>
      ) : generations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/70 rounded-xl text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-history-line text-2xl text-foreground-400"></i>
          </span>
          <p className="text-sm font-medium text-foreground-700">Aucune génération pour l’instant</p>
          <p className="text-xs text-foreground-500 mt-1">Vos générations apparaîtront ici automatiquement.</p>
          <Link to="/dashboard/zstudio/wizard" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-magic-line"></i>Créer maintenant
          </Link>
        </div>
      ) : (
        <div className="space-y-2.5">
          {generations.map((g) => {
            const meta = TYPE_META[(g.type || '').toLowerCase()] || TYPE_META.image;
            const status = STATUS_META[g.status] || STATUS_META.done;
            const url = outputUrl(g);
            const text = outputText(g);
            return (
              <div key={g.id} className="flex flex-col sm:flex-row gap-3 bg-background-50 border border-background-200/70 rounded-xl p-3.5">
                <div className="w-full sm:w-20 h-20 rounded-lg bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {url && (g.type || '').toLowerCase().includes('video') ? (
                    <video src={url} muted className="w-full h-full object-cover object-top" />
                  ) : url ? (
                    <img src={url} alt={g.prompt || 'Génération'} className="w-full h-full object-cover object-top" />
                  ) : (
                    <i className={`${meta.icon} text-2xl text-foreground-400`}></i>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${meta.className}`}>
                      <i className={meta.icon}></i>{meta.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${status.className}`}>
                      <i className={status.icon}></i>{status.label}
                    </span>
                    {g.provider && <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">{g.provider}</span>}
                    <span className="text-[11px] text-foreground-400 ml-auto whitespace-nowrap">{formatDate(g.created_at)}</span>
                  </div>
                  <p className="text-xs text-foreground-700 mt-2 line-clamp-2 break-words">{g.prompt || text || 'Sans description'}</p>
                  {!g.prompt && text && <p className="text-[11px] text-foreground-500 mt-1 line-clamp-2 break-words">{text}</p>}
                </div>

                <div className="flex items-center gap-1 sm:flex-col sm:items-end sm:justify-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => reuse(g)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>Réutiliser
                  </button>
                  <button
                    type="button"
                    onClick={() => duplicate(g)}
                    disabled={busyId === g.id}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    {busyId === g.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-copy-line"></i>}Dupliquer
                  </button>
                  {url && (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
                      <i className="ri-external-link-line"></i>Ouvrir
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}